import i18n from '@/utils/i18n';
import {
  ItemDetailItemInfo,
  SaltInfo,
  SaltNameValuePair,
} from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';

/**
 * One row of the Details tab's product-specs card. `value` is `null` when
 * the underlying field is missing; rows with `null`/empty values are filtered
 * out by {@link buildProductSpecRows} before the card receives them.
 */
export interface ProductSpecRow {
  key: string;
  label: string;
  value: string | null;
}

const findSaltValue = (
  entries: SaltNameValuePair[],
  name: string,
): string | null => {
  const target = name.toLowerCase();
  const match = entries.find((entry) => entry.name.toLowerCase() === target);
  return match?.value || null;
};

/**
 * Flattens the SALT name/value array plus item-level fields into the ordered
 * row list rendered by the Details tab's product-specs card. Row order is
 * fixed so the card reads consistently across items; rows whose value is
 * missing are dropped so empty rows don't show up.
 *
 * @param saltInfo - SALT metadata returned by `getOrderEntryItemDetail`.
 * @param itemInfo - Item header info from the order-entry table fragment.
 * @returns The non-empty rows, in display order.
 */
export const buildProductSpecRows = (
  saltInfo: SaltInfo | null,
  itemInfo: ItemDetailItemInfo,
): ProductSpecRow[] => {
  const additional = saltInfo?.additionalInformation ?? [];
  const upc = itemInfo.upcList[0] ?? null;

  const ordered: ProductSpecRow[] = [
    {
      key: 'brand',
      label: i18n.t('sales_portal.order_entry.item_detail.product_specs.brand'),
      value: findSaltValue(additional, 'Brand'),
    },
    {
      key: 'manufacturer',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.manufacturer',
      ),
      value: findSaltValue(additional, 'Manufacturer'),
    },
    {
      key: 'unit_of_measurement',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.unit_of_measurement',
      ),
      value: findSaltValue(additional, 'Unit of Measurement'),
    },
    {
      key: 'pack_size',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.pack_size',
      ),
      value: itemInfo.packDisplayString || null,
    },
    {
      key: 'average_weight',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.average_weight',
      ),
      value: findSaltValue(additional, 'Average Weight'),
    },
    {
      key: 'gtin',
      label: i18n.t('sales_portal.order_entry.item_detail.product_specs.gtin'),
      value: findSaltValue(additional, 'GTIN'),
    },
    {
      key: 'upc',
      label: i18n.t('sales_portal.order_entry.item_detail.product_specs.upc'),
      value: upc,
    },
    {
      key: 'vendor_item_id',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.vendor_item_id',
      ),
      value: findSaltValue(additional, 'Vendor Item #'),
    },
    {
      key: 'external_item_id',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.external_item_id',
      ),
      value: itemInfo.externalItemId,
    },
    {
      key: 'perishable_days',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.perishable_days',
      ),
      value: findSaltValue(additional, 'Perishable Days'),
    },
    {
      key: 'country_of_origin',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.country_of_origin',
      ),
      value: findSaltValue(additional, 'Country of Origin'),
    },
    {
      key: 'characteristics',
      label: i18n.t(
        'sales_portal.order_entry.item_detail.product_specs.characteristics',
      ),
      value: findSaltValue(additional, 'Characteristics'),
    },
  ];

  return ordered.filter((row) => row.value !== null && row.value !== '');
};
